/**
 * Canonical Structure Utilities
 * 
 * This utility provides functions to convert JSON structures to the canonical format
 * and create canonical structures from form data. This ensures consistency across
 * all workflows in the application.
 */

import { SolutionDetails } from '@/types/solution';
import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Converts any malformed JSON structure to the canonical format
 * Handles multiple broken format patterns and normalizes them
 */
export const convertToCanonicalFormat = (rawSuggestion: any): any => {
  console.log('Converting suggestion to canonical format:', rawSuggestion);
  
  // If it's already in canonical format (has metadata at root), return as-is
  if (rawSuggestion.metadata && !rawSuggestion.details) {
    console.log('Already in canonical format');
    return rawSuggestion;
  }
  
  // Handle the specific broken format from Add Solution (name + shortDescription + details + categoryId + tags at root)
  if (rawSuggestion.name && rawSuggestion.shortDescription && rawSuggestion.details) {
    console.log('Converting broken Add Solution format to canonical');
    
    // Extract tags, handling both array and string formats
    let tags = rawSuggestion.tags || rawSuggestion.details.metadata?.tags || [];
    if (typeof tags === 'string') {
      tags = tags.split(',').map((tag: string) => tag.trim()).filter(Boolean);
    }
    if (!Array.isArray(tags)) {
      tags = [];
    }
    
    const canonicalSuggestion = {
      metadata: {
        name: rawSuggestion.name,
        shortDescription: rawSuggestion.shortDescription,
        tags: tags
      },
      // Spread all content from details at root level
      ...rawSuggestion.details
    };
    
    // Remove nested metadata if it exists (avoid duplication)
    if ('metadata' in canonicalSuggestion && canonicalSuggestion.metadata !== canonicalSuggestion.metadata) {
      delete (canonicalSuggestion as any).metadata;
      // Re-add the correct metadata
      canonicalSuggestion.metadata = {
        name: rawSuggestion.name,
        shortDescription: rawSuggestion.shortDescription,
        tags: tags
      };
    }
    
    console.log('Converted Add Solution format to canonical');
    return canonicalSuggestion;
  }
  
  // If it has the broken format (with details wrapper), convert it
  if (rawSuggestion.details && typeof rawSuggestion.details === 'object') {
    console.log('Converting details wrapper format to canonical');
    
    const canonicalSuggestion = {
      metadata: {
        name: rawSuggestion.name || rawSuggestion.details.metadata?.name || 'Unnamed Solution',
        shortDescription: rawSuggestion.shortDescription || rawSuggestion.details.metadata?.shortDescription || 'No description',
        tags: rawSuggestion.tags || rawSuggestion.details.metadata?.tags || []
      },
      // Spread all content from details at root level
      ...rawSuggestion.details
    };
    
    // Remove metadata from the details if it exists (avoid duplication)
    if (canonicalSuggestion.metadata && 'metadata' in canonicalSuggestion) {
      delete (canonicalSuggestion as any).metadata;
      // Re-add the proper metadata
      canonicalSuggestion.metadata = {
        name: rawSuggestion.name || rawSuggestion.details.metadata?.name || 'Unnamed Solution',
        shortDescription: rawSuggestion.shortDescription || rawSuggestion.details.metadata?.shortDescription || 'No description',
        tags: rawSuggestion.tags || rawSuggestion.details.metadata?.tags || []
      };
    }
    
    return canonicalSuggestion;
  }
  
  // If it's a flat structure without metadata, add metadata wrapper
  if (!rawSuggestion.metadata) {
    console.log('Adding metadata wrapper to flat structure');
    
    return {
      metadata: {
        name: rawSuggestion.name || 'Unnamed Solution',
        shortDescription: rawSuggestion.shortDescription || 'No description',
        tags: rawSuggestion.tags || []
      },
      ...rawSuggestion
    };
  }
  
  console.log('No conversion needed, returning as-is');
  // Return as-is if we can't determine the format
  return rawSuggestion;
};

/**
 * Creates a canonical structure from form data and existing details
 * Used by Add Solution and Edit Suggestion workflows
 */
export const createCanonicalStructure = (formData: {
  name: string;
  shortDescription: string;
  tags?: string[] | string;
  imageUrl?: string;
  details?: any;
}): any => {
  // Handle tags - convert string to array if needed
  let tags: string[] = [];
  if (Array.isArray(formData.tags)) {
    tags = formData.tags;
  } else if (typeof formData.tags === 'string') {
    tags = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);
  }
  
  // Build canonical structure - don't wrap in anti-pattern format
  if (formData.details && typeof formData.details === 'object' && formData.details.metadata) {
    // If details already has canonical structure, use it as-is
    const canonicalStructure = formData.details;
    
    // FIXED: Preserve AI-generated tags if form tags are empty
    const existingTags = canonicalStructure.metadata?.tags || [];
    const tagsToUse = tags.length > 0 ? tags : existingTags;
    
    // Update metadata with form values to ensure consistency
    canonicalStructure.metadata = {
      name: formData.name,
      shortDescription: formData.shortDescription,
      tags: tagsToUse,  // Use form tags only if provided, otherwise preserve existing tags
      imageUrl: formData.imageUrl
    };
    
    return canonicalStructure;
  } else {
    // Create canonical structure from form data
    return {
      metadata: {
        name: formData.name,
        shortDescription: formData.shortDescription,
        tags: tags,
        imageUrl: formData.imageUrl
      },
      // Spread any existing details content at root level
      ...(formData.details || {})
    };
  }
};

/**
 * Converts a canonical format solution (with metadata) to solutions table format (without metadata)
 * Used when accepting review suggestions to store in solutions table
 */
export function convertToSolutionsTableFormat(canonicalSolution: SolutionDetails): Omit<SolutionDetails, 'metadata'> {
  // Destructure to separate metadata from content
  const { metadata, ...contentWithoutMetadata } = canonicalSolution;
  
  // Return only the content fields
  return {
    overview: contentWithoutMetadata.overview || '',
    capabilities: contentWithoutMetadata.capabilities || [],
    useCases: contentWithoutMetadata.useCases || [],
    limitations: contentWithoutMetadata.limitations || [],
    versions: contentWithoutMetadata.versions || [],
    createdBy: contentWithoutMetadata.createdBy || '',
    externalLinks: contentWithoutMetadata.externalLinks || [],
    officialWebsite: contentWithoutMetadata.officialWebsite || { url: '', title: '' },
    longDescription: contentWithoutMetadata.longDescription || ''
  };
}

/**
 * Parameters for reconstructing a canonical solution with metadata
 */
export interface ReconstructCanonicalParams {
  /** The Supabase client instance */
  supabase: SupabaseClient;
  /** The solution ID */
  solutionId: string;
  /** The solution name */
  name: string;
  /** The solution short description */
  shortDescription: string;
  /** The solution's image URL */
  imageUrl?: string;
  /** The solution details object */
  details: any;
  /** Function to fetch tags for a solution */
  getCurrentTags: (supabase: SupabaseClient, solutionId: string) => Promise<string[]>;
}

/**
 * Reconstructs a canonical format solution with metadata from database fields.
 * This is the single source of truth for metadata reconstruction.
 * 
 * @example
 * ```typescript
 * const canonical = await reconstructCanonicalWithMetadata({
 *   supabase,
 *   solutionId: "123",
 *   name: "My Solution",
 *   shortDescription: "A great solution",
 *   details: existingDetails,
 *   getCurrentTags
 * });
 * ```
 */
export async function reconstructCanonicalWithMetadata(params: ReconstructCanonicalParams): Promise<SolutionDetails> {
  const { supabase, solutionId, name, shortDescription, imageUrl, details, getCurrentTags } = params;
  
  // Get tags using the provided function
  const tags = await getCurrentTags(supabase, solutionId);
  
  // Get the details object
  const detailsObj = details || {};
  
  // Reconstruct the canonical format with metadata
  const canonicalSolution = {
    metadata: {
      name,
      shortDescription,
      tags,
      imageUrl: imageUrl || ''
      // Add any future metadata fields here
    },
    // Spread all content from details at root level
    ...detailsObj
  };
  
  return canonicalSolution;
} 